import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { useFlow } from "../context/FlowContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, ArrowRight, Clock, MapPin, Search, Check } from "lucide-react";

const TIME_BUCKETS = ["Morning", "Afternoon", "Evening", "Night", "Don't know"];
const TAROT_TOPICS = [
  { id: "love", emoji: "❤️", label: "Love" },
  { id: "career", emoji: "💼", label: "Career" },
  { id: "money", emoji: "💰", label: "Money" },
  { id: "family", emoji: "🏠", label: "Family" },
  { id: "general", emoji: "🔮", label: "General" },
];

export default function Wizard() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { flow, updateAnswers, update } = useFlow();
  const [tool, setTool] = useState(null);
  const [step, setStep] = useState(0);
  const a = flow.answers;

  useEffect(() => {
    api.get(`/tools/${toolId}`).then(({ data }) => {
      setTool(data);
      update({ tool: data.id, question: flow.question || data.question });
    }).catch(() => {});
    track("reading_flow_opened", { tool_id: toolId });
  }, [toolId]);

  const steps = useMemo(() => {
    if (!tool) return [];
    if (tool.system === "numerology") return ["num_name", "dob"];
    if (tool.system === "tarot") return ["tarot_topic", "tarot_tier"];
    if (tool.system === "compatibility") return ["person_a", "person_b", "compat_tier"];
    return ["name_dob", "time", "place", "context"];
  }, [tool]);

  if (!tool) return <div className="mx-auto max-w-xl px-5 py-20"><div className="h-64 rounded-2xl bg-muted shimmer" /></div>;

  const current = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  const next = () => (step < steps.length - 1 ? setStep(step + 1) : submit());
  const back = () => (step > 0 ? setStep(step - 1) : navigate(-1));

  const submit = async () => {
    const payload = {
      tool_id: tool.id,
      category: flow.category || tool.category,
      sub_question_id: flow.subQuestionId,
      question: flow.question || tool.question,
      name: a.name,
      dob: a.dob,
      birth_time: a.birthTime,
      birth_time_known: a.birthTimeKnown,
      birthplace: a.birthplace,
      context_id: a.contextId,
      context_label: a.contextLabel,
      tier: a.tier,
      person_b: a.personB,
    };
    update({ payload });
    track("reading_started", { tool_id: tool.id });
    navigate("/generating", { state: { payload } });
  };

  return (
    <div className="mx-auto max-w-xl px-5 pt-6 md:pt-12">
      <div className="mb-6 flex items-center gap-3">
        <button data-testid="wizard-back" onClick={back} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-foreground transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div key={current} className="fade-up">
        {current === "name_dob" && <NameDob a={a} updateAnswers={updateAnswers} tool={tool} />}
        {current === "dob" && <DobOnly a={a} updateAnswers={updateAnswers} />}
        {current === "num_name" && <NameStep a={a} updateAnswers={updateAnswers} />}
        {current === "time" && <TimeStep a={a} updateAnswers={updateAnswers} />}
        {current === "place" && <PlaceStep a={a} updateAnswers={updateAnswers} />}
        {current === "context" && <ContextStep a={a} updateAnswers={updateAnswers} toolId={tool.id} onSkip={next} />}
        {current === "tarot_topic" && <TarotTopic a={a} updateAnswers={updateAnswers} />}
        {current === "tarot_tier" && <TarotTier a={a} updateAnswers={updateAnswers} tool={tool} />}
        {current === "person_a" && <PersonStep label="Person A (you)" a={a} field="personA" updateAnswers={updateAnswers} />}
        {current === "person_b" && <PersonStep label="Person B" a={a} field="personB" updateAnswers={updateAnswers} />}
        {current === "compat_tier" && <CompatTier a={a} updateAnswers={updateAnswers} tool={tool} />}
      </div>

      <div className="mt-8">
        <Button
          data-testid="wizard-continue"
          onClick={next}
          disabled={!stepValid(current, a)}
          className="w-full rounded-full bg-foreground py-6 text-base text-background btn-lift hover:bg-foreground disabled:opacity-40"
        >
          {step === steps.length - 1 ? "Create my reading" : "Continue"} <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function stepValid(step, a) {
  switch (step) {
    case "name_dob":
    case "dob":
      return !!a.dob && /^\d{4}-\d{2}-\d{2}$/.test(a.dob);
    case "num_name":
      return (a.name || "").trim().length >= 2;
    case "time":
      return !!a.birthTime;
    case "place":
      return !!a.birthplace;
    case "context":
      return true;
    case "tarot_topic":
      return !!a.topic;
    case "tarot_tier":
    case "compat_tier":
      return a.tier !== undefined;
    case "person_a":
      return a.personA && a.personA.name && a.personA.dob;
    case "person_b":
      return a.personB && a.personB.name && a.personB.dob;
    default:
      return true;
  }
}

/* ---------- step components ---------- */
function DobField({ value, onChange, testid = "dob" }) {
  const parts = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-") : ["", "", ""];
  const [dd, setDd] = useState(parts[2]);
  const [mm, setMm] = useState(parts[1]);
  const [yy, setYy] = useState(parts[0]);
  useEffect(() => {
    if (dd && mm && yy && yy.length === 4) {
      onChange(`${yy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`);
    }
  }, [dd, mm, yy]); // eslint-disable-line
  return (
    <div className="flex gap-3">
      <Input data-testid={`${testid}-dd`} inputMode="numeric" placeholder="DD" maxLength={2} value={dd} onChange={(e) => setDd(e.target.value.replace(/\D/g, ""))} className="rounded-xl bg-background text-center text-lg" />
      <Input data-testid={`${testid}-mm`} inputMode="numeric" placeholder="MM" maxLength={2} value={mm} onChange={(e) => setMm(e.target.value.replace(/\D/g, ""))} className="rounded-xl bg-background text-center text-lg" />
      <Input data-testid={`${testid}-yyyy`} inputMode="numeric" placeholder="YYYY" maxLength={4} value={yy} onChange={(e) => setYy(e.target.value.replace(/\D/g, ""))} className="rounded-xl bg-background text-center text-lg" />
    </div>
  );
}

function NameDob({ a, updateAnswers, tool }) {
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">Let's create your personalized reading</h1>
      <p className="mt-2 text-muted-foreground">Your date of birth helps us create your profile.</p>
      <div className="mt-8 space-y-5">
        <div>
          <label className="mb-2 block text-sm font-medium">Your name <span className="text-muted-foreground">(optional)</span></label>
          <Input data-testid="name-input" value={a.name} onChange={(e) => updateAnswers({ name: e.target.value })} placeholder="First name" className="rounded-xl bg-background" />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium">Date of birth</label>
          <DobField value={a.dob} onChange={(dob) => { updateAnswers({ dob }); track("dob_completed"); }} />
        </div>
      </div>
    </div>
  );
}

function DobOnly({ a, updateAnswers }) {
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">When were you born?</h1>
      <p className="mt-2 text-muted-foreground">Your date of birth powers your numbers.</p>
      <div className="mt-8"><DobField value={a.dob} onChange={(dob) => updateAnswers({ dob })} /></div>
    </div>
  );
}

function NameStep({ a, updateAnswers }) {
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">What's your full name?</h1>
      <p className="mt-2 text-muted-foreground">Numerology uses the letters of your name.</p>
      <Input data-testid="name-input" value={a.name} onChange={(e) => updateAnswers({ name: e.target.value })} placeholder="Full name" className="mt-8 rounded-xl bg-background py-6 text-lg" />
    </div>
  );
}

function TimeStep({ a, updateAnswers }) {
  const [mode, setMode] = useState(a.birthTimeKnown ? "exact" : a.birthTime ? "bucket" : null);
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">What time were you born?</h1>
      <p className="mt-2 text-muted-foreground">An exact birth time allows more detailed chart interpretation.</p>
      <div className="mt-8 grid grid-cols-2 gap-3">
        <button data-testid="time-know" onClick={() => { setMode("exact"); updateAnswers({ birthTimeKnown: true, birthTime: a.birthTime && a.birthTimeKnown ? a.birthTime : "" }); }}
          className={`card-soft rounded-2xl p-4 text-left btn-lift ${mode === "exact" ? "ring-2 ring-gold" : ""}`}>
          <Clock className="h-5 w-5 gold-text" /><div className="mt-2 font-medium">I know my exact time</div>
        </button>
        <button data-testid="time-unsure" onClick={() => { setMode("bucket"); updateAnswers({ birthTimeKnown: false, birthTime: "" }); }}
          className={`card-soft rounded-2xl p-4 text-left btn-lift ${mode === "bucket" ? "ring-2 ring-gold" : ""}`}>
          <Search className="h-5 w-5 gold-text" /><div className="mt-2 font-medium">I'm not sure</div>
        </button>
      </div>
      {mode === "exact" && (
        <Input data-testid="time-input" type="time" value={a.birthTime} onChange={(e) => { updateAnswers({ birthTime: e.target.value, birthTimeKnown: true }); track("birth_time_completed"); }} className="mt-5 rounded-xl bg-background py-6 text-lg" />
      )}
      {mode === "bucket" && (
        <div className="mt-5 flex flex-wrap gap-3">
          {TIME_BUCKETS.map((b) => (
            <button key={b} data-testid={`time-bucket-${b}`} onClick={() => { updateAnswers({ birthTime: b, birthTimeKnown: false }); track("birth_time_completed"); }}
              className={`rounded-full border px-4 py-2 text-sm btn-lift ${a.birthTime === b ? "bg-foreground text-background" : "bg-card"}`}>{b}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function PlaceStep({ a, updateAnswers }) {
  const [q, setQ] = useState(a.birthplace?.city || "");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 1 && q !== a.birthplace?.city) {
        api.get(`/cities?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => {});
      } else setResults([]);
    }, 200);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">Where were you born?</h1>
      <p className="mt-2 text-muted-foreground">Search for your birth city.</p>
      <div className="relative mt-8">
        <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="city-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Lucknow" className="rounded-xl bg-background py-6 pl-10 text-lg" />
      </div>
      {a.birthplace && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold-soft px-4 py-1.5 text-sm">
          <Check className="h-4 w-4" /> {a.birthplace.city}, {a.birthplace.state}
        </div>
      )}
      {results.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-card">
          {results.map((c) => (
            <button key={`${c.city}-${c.lat}`} data-testid={`city-option-${c.city}`} onClick={() => { updateAnswers({ birthplace: c }); setResults([]); setQ(c.city); track("birthplace_completed"); }}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted">
              <span className="font-medium">{c.city}</span><span className="text-sm text-muted-foreground">{c.state}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContextStep({ a, updateAnswers, toolId, onSkip }) {
  const [block, setBlock] = useState(null);
  useEffect(() => {
    api.get("/catalog").then(({ data }) => setBlock(data.context_questions[toolId] || null)).catch(() => {});
  }, [toolId]);
  if (!block) return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">Almost there</h1>
      <p className="mt-2 text-muted-foreground">Tap "Create my reading" to continue.</p>
    </div>
  );
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">{block.prompt}</h1>
      <p className="mt-2 text-muted-foreground">This personalizes the interpretation. It never changes the calculation.</p>
      <div className="mt-8 space-y-3">
        {block.options.map((o) => (
          <button key={o.id} data-testid={`context-${o.id}`} onClick={() => updateAnswers({ contextId: o.id, contextLabel: o.label })}
            className={`card-soft flex w-full items-center gap-4 rounded-2xl p-4 text-left btn-lift ${a.contextId === o.id ? "ring-2 ring-gold" : ""}`}>
            <span className="text-xl">{o.emoji}</span><span className="font-medium">{o.label}</span>
          </button>
        ))}
      </div>
      <button data-testid="context-skip" onClick={() => { updateAnswers({ contextId: null, contextLabel: null }); track("context_completed", { skipped: true }); onSkip(); }}
        className="mt-5 text-sm text-muted-foreground underline hover:text-foreground">Skip</button>
    </div>
  );
}

function TarotTopic({ a, updateAnswers }) {
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">What would you like guidance on?</h1>
      <div className="mt-8 flex flex-wrap gap-3">
        {TAROT_TOPICS.map((t) => (
          <button key={t.id} data-testid={`tarot-topic-${t.id}`} onClick={() => updateAnswers({ topic: t.id, question: t.label })}
            className={`rounded-full border px-5 py-2.5 text-sm btn-lift ${a.topic === t.id ? "bg-foreground text-background" : "bg-card"}`}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        <label className="mb-2 block text-sm font-medium">Or ask one specific question <span className="text-muted-foreground">(optional)</span></label>
        <Input data-testid="tarot-question" onChange={(e) => updateAnswers({ question: e.target.value || a.topic })} placeholder="e.g. Should I take the new job offer?" className="rounded-xl bg-background" />
      </div>
    </div>
  );
}

function TarotTier({ a, updateAnswers, tool }) {
  const options = [
    { tier: null, label: "3-card reading", desc: "Situation · Action · Outcome", price: tool.price },
    { tier: "detailed", label: "5-card reading", desc: "A deeper, fuller spread", price: tool.price_detailed },
  ];
  return <TierPicker a={a} updateAnswers={updateAnswers} options={options} title="Choose your spread" />;
}

function CompatTier({ a, updateAnswers, tool }) {
  const options = [
    { tier: null, label: "Basic compatibility", desc: "Core relationship dimensions", price: tool.price },
    { tier: "detailed", label: "Detailed compatibility", desc: "In-depth descriptive reading", price: tool.price_detailed },
  ];
  return <TierPicker a={a} updateAnswers={updateAnswers} options={options} title="Choose your reading" />;
}

function TierPicker({ a, updateAnswers, options, title }) {
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
      <div className="mt-8 space-y-3">
        {options.map((o) => (
          <button key={o.label} data-testid={`tier-${o.tier || "basic"}`} onClick={() => updateAnswers({ tier: o.tier })}
            className={`card-soft flex w-full items-center justify-between rounded-2xl p-5 text-left btn-lift ${a.tier === o.tier ? "ring-2 ring-gold" : ""}`}>
            <span><span className="font-display text-lg font-semibold">{o.label}</span><div className="text-sm text-muted-foreground">{o.desc}</div></span>
            <span className="font-display text-xl font-semibold">₹{o.price}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PersonStep({ label, a, field, updateAnswers }) {
  const p = a[field] || { name: "", dob: "", birthTime: "", birthplace: null };
  const setP = (patch) => updateAnswers({ [field]: { ...p, ...patch } });
  const [q, setQ] = useState(p.birthplace?.city || "");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 1 && q !== p.birthplace?.city) api.get(`/cities?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => {});
      else setResults([]);
    }, 200);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line
  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">{label}</h1>
      <div className="mt-6 space-y-4">
        <Input data-testid={`${field}-name`} value={p.name} onChange={(e) => setP({ name: e.target.value })} placeholder="Name" className="rounded-xl bg-background" />
        <div><label className="mb-2 block text-sm font-medium">Date of birth</label><DobField testid={`${field}-dob`} value={p.dob} onChange={(dob) => setP({ dob })} /></div>
        <Input data-testid={`${field}-time`} type="time" value={p.birthTime} onChange={(e) => setP({ birthTime: e.target.value })} className="rounded-xl bg-background" />
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid={`${field}-city`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Birth city" className="rounded-xl bg-background pl-10" />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-border bg-card shadow-lg">
              {results.map((c) => (
                <button key={`${c.city}-${c.lat}`} onClick={() => { setP({ birthplace: c }); setQ(c.city); setResults([]); }} className="flex w-full justify-between px-4 py-2.5 text-left hover:bg-muted">
                  <span>{c.city}</span><span className="text-sm text-muted-foreground">{c.state}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
